// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import Dialogs from 'Ui/components/dialogs/dialogs';
import EntryComponent from 'Ui/components/entry/entry-component';
import EntryPublic from 'Ui/components/entry/entry-public';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import ErrorView from 'Ui/components/dashboard/error-view';
import MainContentBackground from 'Ui/components/main-content-background';
import {
    setHasMyJDSession as setHasMyJDSessionRedux,
    setMyJdId as setMyJdIdRedux,
    setPermissions as setPermissionsRedux,
    updateUser as updateUserRedux,
    setMyJdPermissions as setMyJdPermissionsRedux,
    setMyJdUserOrgs as setMyJdUserOrgsRedux,
    setIsgToggles as setIsgTogglesRedux
} from 'Store/actions/account';
import {updateHashedUserValues as updateHashedReduxValues} from 'Store/actions/analytics';
import {updateMembership as updateMembershipRedux} from 'Store/actions/membership';
import {setRumAccount, setRumUser} from 'Services/rum-service';
import {updateUserRoles as updateUserRolesRedux} from 'Store/actions/role';
import {setTranslations as setTranslationsRedux} from 'Store/actions/translations';
import {getIsgToggles as getIsgTogglesForUserAndOrg} from 'Services/isg-toggles-service';
import {getActiveMembership, getSubscriptionForMembership, getUserRoles, syncMembershipAddressFromOrgPreferences} from 'Services/membership-service';
import * as rumService from 'Services/rum-service';
import {getUser as getCurrentUser} from 'Services/user-service';
import {ANALYTICS_EVENTS, trackOnLinkEvent} from 'Utils/analytics-utils';
import {fetchEffectData, useLazyRef} from 'Utils/react-utils';
import {MILLISECONDS_PER_SECOND} from 'Ui/constants/time-constants';
import onlinkImage from 'Ui/images/onlink.png';
import {PRO_GOLF_LABOR_RESKIN} from 'Common/constants/isg-toggles';
import {redirect, getHostname} from 'Utils/redirect-utils';
import {datadogLogs} from '@datadog/browser-logs';
import {debounce} from 'lodash';
import LoadingIcon from 'Ui/components/common/loading-icon';
import {
    getSessionStrategy,
    refreshMyJdPermissions
} from 'Services/session-service';

const LOGIN_PATH = '/login';
const PROFILE_LINK_PATH = '/profile/link';
const WORKBOARD_PRESENTATION_PATH = '/labor/workboard/presentation';
const WORKBOARD_BASE_PATH = '/labor/workboard';
const WORKBOARD_TEMPLATES_PATH = '/labor/workboard/templates';
const WORKBOARD_DRAFTS_PATH = '/labor/workboard/drafts';
const LABOR_SETTINGS_PATH = '/settings/labor/';
const ONLINK_SESSION = 'onlink-session';
const MAP_PATH = '/map';
const SETTINGS_PATH = '/home/details/settings';

async function getMembershipAndRoles(hasMyJDSession) {
    const {
        membership,
        hashedCurrentMembershipId
    } = await getActiveMembership();

    const [
        {userRoles},
        myJdPermissions
    ] = await Promise.all([
        membership.isMigrated ? {
            userRoles: []
        } : getUserRoles(),
        membership.orgId && hasMyJDSession ? refreshMyJdPermissions() : {}
    ]);

    if (membership.orgId && hasMyJDSession) {
        // The POST service catches failures; do not delay app load while syncing the address.
        void syncMembershipAddressFromOrgPreferences();
    }

    return {
        analytics: {
            hashedCurrentMembershipId
        },
        membership,
        userRoles,
        myJdPermissions
    };
}

async function getUser() {
    const {
        appKeyMap,
        appUser,
        hasMyJDSession,
        myJdId,
        permissionMap,
        hashedAppUserId,
        hashedMembershipId,
        myJdUserOrgs
    } = await getCurrentUser();

    return {
        appUser: {
            ...appUser,
            appKeys: appKeyMap
        },
        analytics: {
            hashedAppUserId,
            hashedMembershipId
        },
        myJdId,
        hasMyJDSession,
        membership: appUser.membership,
        permissionMap,
        myJdUserOrgs
    };
}

const getIsgToggles = async (username, orgId) => {
    return await getIsgTogglesForUserAndOrg(username, orgId);
};

async function getRestrictedData(hasMyJDSession) {
    return getMembershipAndRoles(hasMyJDSession);
}

function isLocalHost() {
    return getHostname().includes('local-node-app');
}

function handleOpscenterRedirect(sessionStrategy, opscenterOrPHRedirect, setOpscenterOrPHRedirect, history) {
    if ((sessionStrategy?.strategy === ONLINK_SESSION || isLocalHost()) && opscenterOrPHRedirect.opsCenterSource) {
        setOpscenterOrPHRedirect(null);
        history.replace({
            search: ''
        });
    } else {
        redirect(`/api/myjd-create-session?redirect=${opscenterOrPHRedirect.redirect}`);
    }
}

function handleMapOrSettingsRedirect(setIsMigrated, restrictedData, isMapPage, isSettingsPage) {
    const isMigrated = restrictedData.membership?.isMigrated;

    setIsMigrated(isMigrated);

    if (isMigrated) {
        if (isMapPage) {
            redirect(window.props.mapLink);
        } else if (isSettingsPage) {
            redirect(window.props.teamManagerLink);
        }
    }
}

function validateWorkboardFormPath(pathname) {
    const invalidPaths = [
        WORKBOARD_BASE_PATH,
        `${WORKBOARD_BASE_PATH}/`,
        WORKBOARD_DRAFTS_PATH,
        `${WORKBOARD_DRAFTS_PATH}/`,
        WORKBOARD_TEMPLATES_PATH,
        `${WORKBOARD_TEMPLATES_PATH}/`
    ];

    return !invalidPaths.includes(pathname.toLowerCase()) && !pathname.toLowerCase().includes(LABOR_SETTINGS_PATH);
}

function setReduxState({
    userData,
    restrictedData,
    isgTogglesData,
    isPublicPage,
    translations,
    setHasMyJDSession,
    setMyJdId,
    setPermissions,
    setMyJdUserOrgs,
    updateMembership,
    updateUser,
    setTranslations,
    setMyJdPermissions,
    updateUserRoles,
    setIsgToggles,
    updateHashedUserValues
}) {
    setHasMyJDSession(userData.hasMyJDSession);
    setMyJdId(userData.myJdId);
    setPermissions(userData.permissionMap);
    setMyJdUserOrgs(userData.myJdUserOrgs);
    const activeMembership = restrictedData.membership || userData.membership;

    updateMembership(activeMembership);
    setRumAccount({
        orgId: activeMembership?.orgId,
        name: activeMembership?.name
    });
    updateUser(userData.appUser);
    setRumUser({
        appUserId: userData.appUser?.appUserId,
        membershipId: userData.appUser?.membershipId,
        myJdId: userData.myJdId
    });
    setTranslations(translations);

    if (!isPublicPage) {
        setMyJdPermissions(restrictedData.myJdPermissions);
        updateUserRoles(restrictedData.userRoles);
        setIsgToggles(isgTogglesData);
        updateHashedUserValues({
            ...userData.analytics,
            ...restrictedData.analytics
        });
    }
}

function useEntryWrapperState(initialMembershipId) {
    const [loading, setLoading] = React.useState(true);
    const [isMigrated, setIsMigrated] = React.useState(true);
    const [hasSubscription, setHasSubscription] = React.useState(true);
    const hasCompletedInitialAppLoad = React.useRef(false);
    const lastCheckedMembershipId = React.useRef(initialMembershipId ?? null);

    return {
        hasCompletedInitialAppLoad,
        hasSubscription,
        isMigrated,
        lastCheckedMembershipId,
        loading,
        setHasSubscription,
        setIsMigrated,
        setLoading
    };
}

async function loadUserData({
    isMounted,
    isPublicPage,
    lastCheckedMembershipId,
    setHasSubscription,
    setLoading,
    setIsMigrated,
    isMapPage,
    isSettingsPage,
    translations,
    setHasMyJDSession,
    setMyJdId,
    setPermissions,
    setMyJdUserOrgs,
    updateMembership,
    updateUser,
    setTranslations,
    setMyJdPermissions,
    updateUserRoles,
    setIsgToggles,
    updateHashedUserValues
}) {
    const userData = await getUser();
    const restrictedData = !isPublicPage ? await getRestrictedData(userData.hasMyJDSession) : {
        analytics: {}
    };
    const subscription = isPublicPage ? {
        hasSubscription: true
    } : await getSubscriptionForMembership();

    // Only a successful response saying "no subscription" may blank the dashboard. When the request
    // itself failed we cannot tell, so fall through and let the downstream licensed-feature checks
    // decide rather than replacing the whole app with the no-subscription error view.
    if (isMounted() && !subscription?.hasSubscription && !subscription?.requestFailed) {
        lastCheckedMembershipId.current = restrictedData.membership?.membershipId || null;
        setHasSubscription(false);
        setLoading(false);
        return;
    }

    const isgToggles = restrictedData?.membership?.isMigrated ? await getIsgToggles(userData.myJdId, restrictedData.membership.orgId) : {};

    if (isMounted()) {
        lastCheckedMembershipId.current = restrictedData.membership?.membershipId || null;
        // `undefined` rather than `false` when the lookup failed — the render gate keys off an exact
        // `false` to show the no-subscription error view.
        setHasSubscription(subscription?.requestFailed ? undefined : subscription?.hasSubscription);
        handleMapOrSettingsRedirect(
            setIsMigrated,
            restrictedData,
            isMapPage,
            isSettingsPage
        );
        setReduxState({
            userData,
            restrictedData,
            isgTogglesData: isgToggles,
            isPublicPage,
            translations,
            setHasMyJDSession,
            setMyJdId,
            setPermissions,
            setMyJdUserOrgs,
            updateMembership,
            updateUser,
            setTranslations,
            setMyJdPermissions,
            updateUserRoles,
            setIsgToggles,
            updateHashedUserValues
        });
    }
}

function EntryWrapper(props) {
    const {
        history,
        setIsgToggles,
        setHasMyJDSession,
        setMyJdId,
        setMyJdPermissions,
        setMyJdUserOrgs,
        setPermissions,
        setTranslations,
        updateMembership,
        updateUser,
        updateHashedUserValues,
        updateUserRoles,
        isgToggles,
        membership,
        ...mainNavBarProps
    } = props;

    const [opscenterOrPHRedirect, setOpscenterOrPHRedirect] = React.useState(() => {
        const urlParams = new URLSearchParams(mainNavBarProps.location.search);
        const opsCenterSource = urlParams.get('source') === 'opscenter';
        const opsCenterRedirect = opsCenterSource ? mainNavBarProps.location.pathname : false;
        const redirect = opsCenterRedirect || urlParams.get('next');

        return {
            opsCenterSource,
            redirect
        };
    });

    const debouncedPageChangeTracker = useLazyRef(() => debounce(trackOnLinkEvent, MILLISECONDS_PER_SECOND));
    const {
        hasCompletedInitialAppLoad,
        hasSubscription,
        isMigrated,
        lastCheckedMembershipId,
        loading,
        setHasSubscription,
        setIsMigrated,
        setLoading
    } = useEntryWrapperState(membership?.membershipId);

    const {
        isLoginPage,
        isPublicPage,
        isStandalonePage,
        isSettingsPage,
        isWorkboardForm,
        isMapPage
    } = React.useMemo(() => {
        const pathname = mainNavBarProps.location.pathname;

        const isLoginPage = pathname.startsWith(LOGIN_PATH);
        const isProfileLink = pathname.startsWith(PROFILE_LINK_PATH);
        const isWorkboardPresentation = pathname.startsWith(WORKBOARD_PRESENTATION_PATH);
        const isWorkboardForm = pathname.startsWith(WORKBOARD_BASE_PATH) && validateWorkboardFormPath(pathname);
        const isPublicPage = isLoginPage || isProfileLink;
        const isMapPage = pathname.startsWith(MAP_PATH);
        const isSettingsPage = pathname.startsWith(SETTINGS_PATH);

        return {
            isLoginPage,
            isPublicPage,
            isSettingsPage,
            isMapPage,
            isWorkboardForm,
            isStandalonePage: isWorkboardPresentation
        };
    }, [mainNavBarProps.location.pathname]);

    React.useEffect(() => {
        const {
            pathname, search
        } = mainNavBarProps.location;
        const query = new URLSearchParams(search);

        if (pathname === LOGIN_PATH && !query.has('error')) {
            const redirectPath = query.get('redirect');
            const loginUrl = redirectPath ?
                `/myjd-login?redirect=${encodeURIComponent(redirectPath)}` :
                '/myjd-login';

            redirect(loginUrl);
        }
    }, [mainNavBarProps.location.pathname, mainNavBarProps.location.search]);

    React.useEffect(() => {
        rumService.startLoadTracking({
            isPublicPage,
            route: mainNavBarProps.location.pathname
        });

        if (!hasCompletedInitialAppLoad.current) {
            rumService.completeAppLoad({
                isPublicPage,
                route: mainNavBarProps.location.pathname
            });
            hasCompletedInitialAppLoad.current = true;
        }
    }, [isPublicPage, mainNavBarProps.location.pathname]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);

        // fetchEffectData does not catch, so without this an initial-load rejection left `loading`
        // true forever and the app rendered nothing but its spinner.
        try {
            if (opscenterOrPHRedirect?.redirect) {
                const sessionStrategy = await getSessionStrategy();

                handleOpscenterRedirect(sessionStrategy, opscenterOrPHRedirect, setOpscenterOrPHRedirect, history);
            } else if (!isLoginPage) {
                await loadUserData({
                    isMounted,
                    isPublicPage,
                    lastCheckedMembershipId,
                    setHasSubscription,
                    setLoading,
                    setIsMigrated,
                    isMapPage,
                    isSettingsPage,
                    translations: mainNavBarProps.translations,
                    setHasMyJDSession,
                    setMyJdId,
                    setPermissions,
                    setMyJdUserOrgs,
                    updateMembership,
                    updateUser,
                    setTranslations,
                    setMyJdPermissions,
                    updateUserRoles,
                    setIsgToggles,
                    updateHashedUserValues
                });
            }
        } catch (e) {
            datadogLogs.logger.error('Initial app load failed', {
                route: mainNavBarProps.location.pathname
            }, e);
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }), [
        opscenterOrPHRedirect,
        isMapPage,
        isSettingsPage,
        isPublicPage,
        mainNavBarProps.translations,
        setHasMyJDSession,
        setMyJdId,
        setPermissions,
        setMyJdUserOrgs,
        updateMembership,
        updateUser,
        setTranslations,
        setMyJdPermissions,
        updateUserRoles,
        setIsgToggles,
        updateHashedUserValues
    ]);

    React.useEffect(() => {
        if (!membership?.membershipId) {
            return undefined;
        }

        if (membership.membershipId === lastCheckedMembershipId.current) {
            return undefined;
        }

        return fetchEffectData(async (isMounted) => {
            setLoading(true);

            try {
                const subscription = await getSubscriptionForMembership();

                if (isMounted()) {
                    lastCheckedMembershipId.current = membership.membershipId;
                    setHasSubscription(subscription?.requestFailed ? undefined : Boolean(subscription?.hasSubscription));
                }
            } finally {
                if (isMounted()) {
                    setLoading(false);
                }
            }
        });
    }, [membership?.membershipId]);

    React.useEffect(() => {
        if (isgToggles && isgToggles[PRO_GOLF_LABOR_RESKIN]) {
            rumService.enableSessionReplay();
        }

        if (!loading) {
            if (!isPublicPage) {
                debouncedPageChangeTracker.current(`${ANALYTICS_EVENTS.PAGE_LOADED}`);
            }

            rumService.completeDataLoad({
                isPublicPage,
                route: mainNavBarProps.location.pathname
            });
        }
    }, [loading, isPublicPage, isgToggles, mainNavBarProps.location.pathname]);

    if (hasSubscription === false) {
        return (
            <>
                <div className='main-page-header'>
                    <ErrorView translations={mainNavBarProps.translations}/>
                </div>
                <Dialogs/>
            </>
        );
    }

    if (opscenterOrPHRedirect?.redirect || hasSubscription && isMigrated && (isMapPage || isSettingsPage)) {
        return (
            <div className='main-page-header'>
                <LoadingIcon
                    className='dashboard-loading-icon'
                    size='50px'
                />
            </div>
        );
    }

    return isPublicPage ? (
        <>
            <div className='main-page-public'>
                <div className='main-navbar public-navbar'>
                    <img
                        alt='OnLink Logo'
                        className='onlink-image'
                        src={onlinkImage}
                    />
                </div>
                <MainContentBackground/>
                <LoadingWrapper
                    className='dashboard-loading-icon'
                    loading={loading}
                    size='50px'
                >
                    <EntryPublic
                        location={mainNavBarProps.location}
                        translations={mainNavBarProps.translations}
                    />
                </LoadingWrapper>
            </div>
            <Dialogs/>
        </>
    ) : (
        <>
            <div className='main-page-header'>
                <LoadingWrapper
                    className='dashboard-loading-icon'
                    loading={loading}
                    size='50px'
                >
                    <EntryComponent
                        hasSubscription={hasSubscription}
                        isStandalonePage={isStandalonePage}
                        isWorkboardForm={isWorkboardForm}
                        location={mainNavBarProps.location}
                        mainNavBarProps={mainNavBarProps}
                        translations={mainNavBarProps.translations}
                    />
                </LoadingWrapper>
            </div>
            <Dialogs/>
        </>
    );
}

EntryWrapper.propTypes = {
    history: PropTypes.history,
    isgToggles: PropTypes.object,
    location: PropTypes.location,
    membership: PropTypes.membership,
    setHasMyJDSession: PropTypes.func,
    setIsgToggles: PropTypes.func,
    setMyJdId: PropTypes.func,
    setMyJdPermissions: PropTypes.func,
    setMyJdUserOrgs: PropTypes.func,
    setPermissions: PropTypes.func,
    setTranslations: PropTypes.func,
    translations: PropTypes.translations,
    updateHashedUserValues: PropTypes.func,
    updateMembership: PropTypes.func,
    updateUser: PropTypes.func,
    updateUserRoles: PropTypes.func
};

export function mapDispatchToProps(dispatch) {
    return {
        setIsgToggles(value) {
            dispatch(setIsgTogglesRedux(value));
        },
        setHasMyJDSession(value) {
            dispatch(setHasMyJDSessionRedux(value));
        },
        setMyJdUserOrgs(value) {
            dispatch(setMyJdUserOrgsRedux(value));
        },
        setMyJdId(value) {
            dispatch(setMyJdIdRedux(value));
        },
        setMyJdPermissions(value) {
            dispatch(setMyJdPermissionsRedux(value));
        },
        setPermissions(value) {
            dispatch(setPermissionsRedux(value));
        },
        setTranslations(value) {
            dispatch(setTranslationsRedux(value));
        },
        updateMembership(value) {
            dispatch(updateMembershipRedux(value));
        },
        updateUser(value) {
            dispatch(updateUserRedux(value));
        },
        updateHashedUserValues(value) {
            dispatch(updateHashedReduxValues(value));
        },
        updateUserRoles(value) {
            dispatch(updateUserRolesRedux(value));
        }
    };
}

export function mapStateToProps(state) {
    return {
        isgToggles: state.account.isgToggles,
        membership: state.membership
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EntryWrapper));
